import { fetchWithAuth, BASE_URL } from './client';

/**
 * Fetch all partners for the current user
 * GET /api/partners
 */
export async function fetchPartners(options = {}) {
    const response = await fetchWithAuth(`${BASE_URL}/api/partners`, {
        cache: 'no-store',
        skipGlobalAuthHandler: options.skipGlobalAuthHandler || false
    });
    // Gracefully handle 404 - endpoint may not exist yet
    if (response.status === 404) {
        return { data: { partners: [] } };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch partners: ${response.statusText}`);
    }
    try {
        return await response.json();
    } catch {
        return { data: { partners: [] } };
    }
}

/**
 * Fetch partners dashboard summary with stats
 * GET /api/partners/dashboard
 */
export async function fetchPartnersDashboard(options = {}) {
    const response = await fetchWithAuth(`${BASE_URL}/api/partners/dashboard`, {
        cache: 'no-store',
        skipGlobalAuthHandler: options.skipGlobalAuthHandler || false
    });
    if (response.status === 404) {
        return {
            data: {
                partners: [],
                stats: {
                    total: 0,
                    active: 0,
                    paused: 0,
                    totalLeadsAccessed: 0,
                    totalLeadsConverted: 0,
                    conversionRate: 0
                }
            }
        };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch partners dashboard: ${response.statusText}`);
    }
    try {
        return await response.json();
    } catch {
        return { data: { partners: [], stats: {} } };
    }
}

/**
 * Fetch a single partner by email with optional leads
 * GET /api/partners/{email}?include_leads=true
 */
export async function fetchPartner(email, includeLeads = false) {
    const params = includeLeads ? '?include_leads=true' : '';
    const response = await fetchWithAuth(`${BASE_URL}/api/partners/${encodeURIComponent(email)}${params}`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return null;
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch partner: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Add a new partner
 * POST /api/partners
 */
export async function addPartner(data) {
    const response = await fetchWithAuth(`${BASE_URL}/api/partners`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        if (response.status === 400) {
            const errorData = await response.json();
            throw new Error(errorData?.detail?.message || errorData?.message || "Partner already exists.");
        }
        if (response.status === 403) {
            const errorData = await response.json();
            throw new Error(errorData?.detail?.message || "Feature requires Ultra plan or higher.");
        }
        throw new Error(`Failed to add partner: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Update a partner
 * PATCH /api/partners/{email}
 */
export async function updatePartner(email, data) {
    const response = await fetchWithAuth(`${BASE_URL}/api/partners/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });
    if (response.status === 404) {
        throw new Error("Partner not found.");
    }
    if (!response.ok) {
        throw new Error(`Failed to update partner: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Delete a partner
 * DELETE /api/partners/{email}
 */
export async function deletePartner(email) {
    const response = await fetchWithAuth(`${BASE_URL}/api/partners/${encodeURIComponent(email)}`, {
        method: 'DELETE'
    });
    if (response.status === 404) {
        throw new Error("Partner not found.");
    }
    if (!response.ok) {
        throw new Error(`Failed to delete partner: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Pause a partner (disable their access)
 * POST /api/partners/{email}/pause
 */
export async function pausePartner(email) {
    const response = await fetchWithAuth(`${BASE_URL}/api/partners/${encodeURIComponent(email)}/pause`, {
        method: 'POST'
    });
    if (response.status === 404) {
        throw new Error("Partner not found.");
    }
    if (!response.ok) {
        throw new Error(`Failed to pause partner: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Resume a partner (re-enable their access)
 * POST /api/partners/{email}/resume
 */
export async function resumePartner(email) {
    const response = await fetchWithAuth(`${BASE_URL}/api/partners/${encodeURIComponent(email)}/resume`, {
        method: 'POST'
    });
    if (response.status === 404) {
        throw new Error("Partner not found.");
    }
    if (!response.ok) {
        throw new Error(`Failed to resume partner: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Fetch leads accessed by a specific partner
 * GET /api/partners/{email}/leads
 */
export async function fetchPartnerLeads(email) {
    const response = await fetchWithAuth(`${BASE_URL}/api/partners/${encodeURIComponent(email)}/leads`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: { leads: [] } };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch partner leads: ${response.statusText}`);
    }
    try {
        return await response.json();
    } catch {
        return { data: { leads: [] } };
    }
}

/**
 * Fetch email history for a realtor partner
 * GET /api/leads/email-history/realtor/{realtor_email}
 * @param {string} realtorEmail - The realtor's email address
 * @param {Object} options - Query options
 * @param {number} options.limit - Max emails to return (1-200, default 50)
 * @returns {Promise<{success: boolean, emails: Array, total: number}>}
 */
export async function fetchRealtorEmailHistory(realtorEmail, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString();
    const url = `${BASE_URL}/api/leads/email-history/realtor/${encodeURIComponent(realtorEmail)}${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, {
        cache: 'no-store'
    });

    if (response.status === 404) {
        return { success: true, emails: [], total: 0 };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch email history: ${response.statusText}`);
    }
    try {
        return await response.json();
    } catch {
        return { success: true, emails: [], total: 0 };
    }
}

/**
 * Fetch email history for an insurance agent partner
 * GET /api/leads/email-history/insurance/{insurance_email}
 * @param {string} insuranceEmail - The insurance agent's email address
 * @param {Object} options - Query options
 * @param {number} options.limit - Max emails to return (1-200, default 50)
 * @returns {Promise<{success: boolean, emails: Array, total: number}>}
 */
export async function fetchInsuranceEmailHistory(insuranceEmail, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString();
    const url = `${BASE_URL}/api/leads/email-history/insurance/${encodeURIComponent(insuranceEmail)}${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, {
        cache: 'no-store'
    });

    if (response.status === 404) {
        return { success: true, emails: [], total: 0 };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch email history: ${response.statusText}`);
    }
    try {
        return await response.json();
    } catch {
        return { success: true, emails: [], total: 0 };
    }
}
